import React, { useEffect, useRef } from "react";

import EasyMDE from "easymde";

import "../../css/admin/markdown-editor.scss";

import type { DOMEvent, Editor } from "codemirror";
import type { Options } from "easymde";
import type { JSX } from "react";

type CodemirrorEvents =
  | "change"
  | "changes"
  | "beforeChange"
  | "cursorActivity"
  | "beforeSelectionChange"
  | "viewportChange"
  | "gutterClick"
  | "focus"
  | "blur"
  | "scroll"
  | "update"
  | "renderLine";

type SimpleMdeToCodemirror = Partial<
  Record<CodemirrorEvents | DOMEvent, Editor["on"]>
>;

export interface SimpleMDEEditorProps {
  id?: string;
  value?: string;
  onChange: (value: string) => void;
  options?: Options;
  className?: string;
  events?: SimpleMdeToCodemirror;
  getMdeInstance?: (instance: EasyMDE) => void;
}

export default function SimpleMDEEditor({
  id,
  value,
  onChange,
  options,
  className,
  events,
  getMdeInstance,
}: SimpleMDEEditorProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const easyMdeRef = useRef<EasyMDE | null>(null);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!textareaRef.current) return undefined;

    const easyMde = new EasyMDE({
      element: textareaRef.current,
      initialValue: value ?? "",
      ...options,
    });

    easyMdeRef.current = easyMde;

    const handleChange = (): void => {
      onChangeRef.current(easyMde.value());
    };
    easyMde.codemirror.on("change", handleChange);

    if (events) {
      Object.entries(events).forEach(([eventName, callback]) => {
        if (eventName && callback) {
          easyMde.codemirror.on(
            eventName as DOMEvent,
            callback as unknown as Parameters<Editor["on"]>[1],
          );
        }
      });
    }

    getMdeInstance?.(easyMde);

    return (): void => {
      easyMde.codemirror.off("change", handleChange);
      easyMde.toTextArea();
      easyMdeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const easyMde = easyMdeRef.current;
    if (!easyMde) return;
    if (easyMde.value() !== (value ?? "")) {
      easyMde.value(value ?? "");
    }
  }, [value]);

  return (
    <div className={className}>
      <textarea ref={textareaRef} id={id} />
    </div>
  );
}
